import { useMemo, useState } from "react";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import {
  useCreateTaskMutation,
  useCreateTaskSectionMutation,
  useDeleteTaskMutation,
  useDeleteTaskSectionMutation,
  useGetSectionHierarchyQuery,
  useLazyGetTaskByIdQuery,
  usePatchTaskMutation,
  useUpdateTaskSectionMutation,
} from "shared/api";
import { SectionHierarchyParams } from "shared/api/task-hub/models";
import { useConfirmDeleteModal } from "./useConfirmDeleteModal";
import { useTaskHubState } from "./useTaskHubState";
import {
  buildTreeRows,
  collectDescendantSectionIds,
  flattenSections,
} from "../../pages/ProductCards/ProductCards/ui/TaskHub/taskHubTreeUtils";
import { extractIds } from "../../pages/ProductCards/ProductCards/ui/TaskHub/taskHubHelpers";
import {
  SectionFormValues,
  SectionRow,
  TaskRow,
} from "../../pages/ProductCards/ProductCards/ui/TaskHub/types";

type SectionMode = "create" | "edit";

/**
 * Encapsulates all data, mutations and handlers for the Task Hub tree view so
 * the `TaskHubTree` component stays presentational. Returns the derived rows,
 * the section-modal state and the callback bundle consumed by
 * `useTaskHubTreeColumns`.
 */
export const useTaskHubTree = (
  modalState: ReturnType<typeof useTaskHubState>,
  params?: SectionHierarchyParams
) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();

  const { data, isLoading, refetch } = useGetSectionHierarchyQuery(params);

  const [createTask] = useCreateTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [patchTask] = usePatchTaskMutation();
  const [fetchTaskById] = useLazyGetTaskByIdQuery();
  const [createSection, { isLoading: creatingSection }] =
    useCreateTaskSectionMutation();
  const [updateSection, { isLoading: updatingSection }] =
    useUpdateTaskSectionMutation();
  const [deleteSection] = useDeleteTaskSectionMutation();

  const [addTaskParent, setAddTaskParent] = useState<number | null>(null);

  // Section modal state
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [sectionMode, setSectionMode] = useState<SectionMode>("create");
  const [editingSection, setEditingSection] = useState<SectionRow | null>(null);
  const [createParentDefault, setCreateParentDefault] = useState<number | null>(
    null
  );

  const root = data?.sections;
  const rows = useMemo(() => buildTreeRows(root), [root]);
  const flatSections = useMemo(() => flattenSections(root), [root]);

  const showError = (message: string, err: unknown) => {
    const description =
      (err as { data?: { detail?: string } })?.data?.detail ||
      t("taskHub.sections.actionFailed") ||
      "The action could not be completed.";
    api.error({ message, description, placement: "topRight" });
    // eslint-disable-next-line no-console
    console.error(message, err);
  };

  /** Run a mutation and surface a notification on failure. */
  const runSafely = async (
    action: () => Promise<unknown>,
    errorMessage: string
  ) => {
    try {
      await action();
    } catch (err) {
      showError(errorMessage, err);
    }
  };

  // ---- Tasks ----
  const onToggleTask = (task: TaskRow) =>
    runSafely(
      () => patchTask({ id: task.id, enabled: !task.enabled }).unwrap(),
      t("taskHub.toggleFailed") || "Failed to toggle status"
    );

  const onMoveTask = (id: number, target: number | null) =>
    runSafely(
      () => patchTask({ id, parent_section: target }).unwrap(),
      t("taskHub.sections.moveFailed") || "Failed to move task"
    );

  const onDuplicateTask = (task: TaskRow) =>
    runSafely(async () => {
      const full = await fetchTaskById({
        id: task.id,
        expand: "machines,product_cards,files,users,companies",
      }).unwrap();

      await createTask({
        name: `${full.name} (copy)`,
        trigger: full.trigger ?? null,
        periodicity: full.periodicity,
        periodicity_multiple: full.periodicity_multiple ?? null,
        is_recurrent: full.is_recurrent,
        start_periodicity: full.start_periodicity,
        priority: full.priority,
        enabled: full.enabled,
        description: full.description,
        users: extractIds(full.users, "user"),
        machines: extractIds(full.machines, "machine"),
        product_cards: extractIds(full.product_cards, "product_card"),
        companies: extractIds(full.companies, "company"),
        parent_section: task.parent_section,
      }).unwrap();
    }, t("taskHub.duplicateFailed") || "Failed to duplicate task");

  const confirmDeleteTask = useConfirmDeleteModal(
    t("Are you sure you want to delete this task?"),
    (id: number) =>
      runSafely(
        () => deleteTask(id).unwrap(),
        t("taskHub.deleteFailed") || "Failed to delete task"
      )
  );

  const onEditTask = (id: number) => {
    modalState.setEditingTask(id);
    modalState.setIsEditModalOpen(true);
  };

  const onAddTaskInSection = (sectionId: number) => {
    setAddTaskParent(sectionId);
    modalState.setIsAddModalOpen(true);
  };

  const closeAddTask = () => {
    modalState.setIsAddModalOpen(false);
    setAddTaskParent(null);
  };

  // ---- Sections ----
  const openCreateSection = (parent: number | null) => {
    setSectionMode("create");
    setEditingSection(null);
    setCreateParentDefault(parent);
    setSectionModalOpen(true);
  };

  const openEditSection = (section: SectionRow) => {
    setSectionMode("edit");
    setEditingSection(section);
    setSectionModalOpen(true);
  };

  const submitSection = (values: SectionFormValues) =>
    runSafely(async () => {
      if (sectionMode === "create") {
        await createSection(values).unwrap();
      } else if (editingSection) {
        await updateSection({ id: editingSection.id, ...values }).unwrap();
      }
      setSectionModalOpen(false);
    }, t("taskHub.sections.saveFailed") || "Failed to save section");

  const onMoveSection = (id: number, target: number | null) =>
    runSafely(
      () => updateSection({ id, parent_section: target }).unwrap(),
      t("taskHub.sections.moveFailed") || "Failed to move section"
    );

  const confirmDeleteSection = useConfirmDeleteModal(
    t("taskHub.sections.confirmDelete") ||
      "Are you sure you want to delete this section? Its tasks and subsections may be affected.",
    (id: number) =>
      runSafely(
        () => deleteSection(id).unwrap(),
        t("taskHub.sections.deleteFailed") || "Failed to delete section"
      )
  );

  const getDisallowedSectionIds = (sectionId: number) =>
    collectDescendantSectionIds(root, sectionId);

  // When editing, a section cannot be moved into itself or one of its
  // descendants.
  const sectionOptions = useMemo(() => {
    if (sectionMode === "edit" && editingSection) {
      const disallowed = collectDescendantSectionIds(root, editingSection.id);
      return flatSections.filter((s) => !disallowed.has(s.id));
    }
    return flatSections;
  }, [sectionMode, editingSection, flatSections, root]);

  const sectionInitialValues: SectionFormValues =
    sectionMode === "edit" && editingSection
      ? {
          name: editingSection.name,
          parent_section: editingSection.parent_section,
        }
      : { name: "", parent_section: createParentDefault };

  return {
    contextHolder,
    rows,
    flatSections,
    isLoading,
    refetch,
    addTaskParent,
    closeAddTask,
    getDisallowedSectionIds,
    handlers: {
      onAddTaskInSection,
      onAddSubsection: openCreateSection,
      onEditSection: openEditSection,
      onDeleteSection: (id: number) => confirmDeleteSection(id),
      onMoveSection,
      onEditTask,
      onDeleteTask: (id: number) => confirmDeleteTask(id),
      onDuplicateTask,
      onMoveTask,
      onToggleTask,
    },
    section: {
      open: sectionModalOpen,
      mode: sectionMode,
      options: sectionOptions,
      loading: creatingSection || updatingSection,
      initialValues: sectionInitialValues,
      openCreate: openCreateSection,
      close: () => setSectionModalOpen(false),
      submit: submitSection,
    },
  };
};
